import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";
import { createFamily, getCurrentUser } from "@/lib/firebase";
import { useAuth } from "@/contexts/AuthContext";

export default function OnboardingPage() {
  const [step, setStep] = useState(1);
  const [familyName, setFamilyName] = useState("");
  const [familyDescription, setFamilyDescription] = useState("");
  const [familyIcon, setFamilyIcon] = useState("👨‍👩‍👧‍👦");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const navigate = useNavigate();
  const { toast } = useToast();
  const { refreshUser } = useAuth();
  
  const handleNext = () => {
    if (step === 1 && !familyName) {
      toast({
        title: "Missing Fields",
        description: "Please enter a family name to continue.",
        variant: "destructive",
      });
      return;
    }
    
    setStep(step + 1);
  };
  
  const handleBack = () => {
    setStep(step - 1);
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!firstName || !lastName) {
      toast({
        title: "Missing Fields",
        description: "Please fill out all required fields.",
        variant: "destructive",
      });
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const firebaseUser = await getCurrentUser();
      
      if (!firebaseUser) {
        throw new Error("User not authenticated");
      }
      
      await createFamily(
        familyName,
        familyDescription,
        familyIcon,
        firebaseUser.uid,
        firebaseUser.email || "",
        firstName,
        lastName,
        firebaseUser.photoURL || ""
      );
      
      // Refresh user data to ensure the auth context has the updated user info
      await refreshUser();
      
      toast({
        title: "Setup Complete!",
        description: "Your family account has been created.",
      });
      
      // Use replace to avoid going back to onboarding
      navigate("/dashboard", { replace: true });
    } catch (error) {
      console.error("Error setting up family:", error);
      toast({
        title: "Error",
        description: "Failed to create family. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-b from-shekel-50 to-white p-4">
      <div className="max-w-md w-full bg-white p-8 rounded-xl shadow-lg">
        <div className="text-center mb-6">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-shekel-600 rounded-full flex items-center justify-center">
              <span className="text-white text-3xl font-bold">₪</span>
            </div>
          </div>
          <h1 className="text-2xl font-bold text-shekel-800">Welcome to Shekel</h1>
          <p className="text-gray-500">Let's set up your family account</p>
        </div>
        
        {step === 1 && (
          <div className="space-y-4">
            <h2 className="font-semibold text-xl">Family Details</h2>
            <div className="space-y-2">
              <Label htmlFor="familyName">Family Name *</Label>
              <Input
                id="familyName"
                value={familyName}
                onChange={(e) => setFamilyName(e.target.value)}
                placeholder="e.g. The Smiths"
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="familyDescription">Description</Label>
              <Textarea
                id="familyDescription"
                value={familyDescription}
                onChange={(e) => setFamilyDescription(e.target.value)}
                placeholder="A brief description of your family"
                rows={3}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="familyIcon">Family Icon (emoji)</Label>
              <Input
                id="familyIcon"
                value={familyIcon}
                onChange={(e) => setFamilyIcon(e.target.value)}
                placeholder="👨‍👩‍👧‍👦"
                maxLength={2}
              />
            </div>
            
            <Button className="w-full mt-4" onClick={handleNext}>
              Next
            </Button>
          </div>
        )}
        
        {step === 2 && (
          <form onSubmit={handleSubmit} className="space-y-4">
            <h2 className="font-semibold text-xl">Your Details</h2>
            <div className="space-y-2">
              <Label htmlFor="firstName">First Name *</Label>
              <Input
                id="firstName"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="lastName">Last Name *</Label>
              <Input
                id="lastName"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
              />
            </div>
            
            <div className="flex space-x-2 pt-4">
              <Button type="button" variant="outline" onClick={handleBack} className="flex-1">
                Back
              </Button>
              <Button type="submit" className="flex-1" disabled={isSubmitting}>
                {isSubmitting ? "Creating..." : "Complete Setup"}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
